import {Setup} from '@/core/main/Setup'
import {Dimension, Options} from '@/core/main/Interface'
import {
	THREE_D_URL,
	APP_LOCATION,
	APP_ROLE,
	APP_USE,
	SERIES_ID,
	USER_INTEREST
} from '@/core/config/constants'
import {Emitter} from '@/core/util/Emitter'
import {Debug} from '@/core/util/Debug'
import {Design} from '@/core/main/Design'
import {Manipulate} from '@/core/main/Manipulate'
import {Reporting} from '@/core/util/Reporting'
import {Print} from '@/core/main/Print'
import {Roof} from '@/core/items/base/Roof'
import {Elevation} from '@/core/main/Elevation'
import {DesignNotes} from '@/core/main/DesignNotes'
import {DesignCodes} from '@/core/main/DesignCodes'


import * as svgjs from '@svgdotjs/svg.js'

/**
 *
 * =========== THIS AREA FOR DESIGN NOTES AND REFERENCE ========
 *
 * -- how to set levels/heights/decks in Builder.
 *
 *  For Commercial Outdoor, Under2 and Serendipity there are only a few levels in the playset,
 *  and any structure has fixed levels that cannot change. These lines are easy to work with as
 *  accessories can only attach to specific structures that will not change their deck heights.
 *
 *  Frolic and the Indoor lines are different - the structures can have decks of varying heights
 *  and can also have multiple decks on some structures. This makes attaching accessories,
 *  fences etc challenging, as if the deck heights change during the design process, the accessories
 *  and fences attached to that structure can become illegal.
 *
 *  In addition, these lines allow structures to attach directly to other structures, to the extent
 *  that they share certain parts (posts, braces).
 *
 *  Because of this, we need to track structure heights and accessory heights carefully so that
 *  at any stage of the design process we can check the validity of all attachments.
 *
 *  APP VARIABLES:
 *
 *  _deck_heights:  number[]
 *  allows us to find the highest deck of an item so that we can calculate post heights,
 *  and whether a neighboring item has taller posts etc.
 * Only Frolic uses _deck_heights at this point.
 *
 *
 *  _deck_height:  number
 *  used by CO, U2 and SY to determine if an accessory can be placed.
 *
 *
 *  itemLevel: number
 *  used as a store for the GROUND/DECK levels, stored with the design data as item_level.
 *  Read when item is created from a saved design
 *
 * FROLIC, INDOOR VARIABLES:
 *
 *  _decks:  string[]
 *  used to track the actual decks - including picnic table, so needs to be a string array.
 *
 *
 *   _deck_map:  string[]
 *   a Map of the possible deck labels that a structure can have.
 *
 *
 *  _levels:  number
 * used as a reference for attaching items to know if they can attach at the available heights.
 *
 *
 *   _valid_levels:  number[]
 *   a numeric array that tells us what levels an item can attach to.
 *
 *
 *   _bays:  Map<string, IBay>
 * export interface IBay {
		active: boolean;
		posts: string[];
		upper_brace: number;
		lower_brace: boolean;
		braces: number[]
	}
 *    the braces variable of IBay keeps track of the braces shared by the structure and any structures
 *    that neighbor it. It is used for elevations, 3D, packlists etc.
 *
 */


export class Init {

	//public static app_version: string = '0.1.1'
	//  _WP_SERIES_ID is passed in at compile time from WebPack
	// @ts-ignore
	public static series_id: number = _WP_SERIES_ID;

	//  _WP_APP_VERSION is passed in at compile time from WebPack
	// @ts-ignore
	public static app_version: string = _WP_APP_VERSION;
	//_WP_APP_NAME is passed in at compile time from WebPack
	// it is used to save the app name and version with every design
	// @ts-ignore
	public static app_file_name: string = _WP_APP_NAME;
	public static env: string = 'dev';

	public static item_image_path: string = '/images/guide/';
	public static line_name: string = '';
	public static line_color: string = '';
	public static core_version: string = '';
	public static line_version: string = '';
	public static lib_import_path: string = '';

	public static builder_version: string = '';
	public static design_id: string = '';
	public static design_name: string = '';
	public static design_timestamp: number;
	public static design_source: string = 'www';
	public static design_source_host: string = '';
	public static app_location: string = 'office';
	public static app_role: string = 'design';
	public static app_use: string = 'design';
	public static base_url: string = '';

	public static primary_menu_id: string = '3';

	public static user_auth_status: boolean;
	public static user_auth_role: number;
	public static user_auth_email: string;
	public static user_auth_id: number = null;
	public static user_interest: string = '';
	public static indoor_outdoor: number;


	public static emitter: Emitter;
	public static debug: Debug;
	public static set_design: Design;
	public static reporting: Reporting;
	public static print: Print;
	public static elevation: Elevation;
	public static active_roof: Roof;
	public static manipulate: Manipulate;

	public static svg_stage: svgjs.Container;
	public static menu: svgjs.Container;

	public static _3D_url: string = '';

	public static keysdown: string[];
	public static design_scale: number = 1;
	public static active_level: number = 1;
	public static active_item: string = '';
	public static print_views: string[] = ['diagram', 'ground', 'roof', 'bays'];

	public static option_mode_active: number = null;
	public static active_option_item: any;
	public static design_notes: DesignNotes;
	public static design_codes: DesignCodes;
	public static options_active: boolean = false;
	public static is_touch_device: boolean = false;
	public static notes_active: boolean = false;
	public static codes_active: boolean = false;
	public static roofs_active: boolean = false;
	public static fences_active: boolean = false;
	public static playhouse_active: boolean = false;
	public static tmp_hitpoints_active: boolean = false;
	public static floors_active: boolean = false;
	public static decks_active: boolean = false;
	public static debug_active: boolean = false;
	public static print_active: boolean = false;
	public static tools_active: boolean = false;
	public static labels_active: boolean = false;
	public static autofence_active: boolean = false;
	public static modal_active: boolean = false;
	public static view_mode: string = '';
	public static design_mode: string = '';
	public static my_cw_url: string = '';
	public static stage_color: string = '#d5dcd3';
	public static menu_primary_id: string = '1';
	public static _hide_menu_category: number;
	public _id: string;
	public static x: any;
	public static line: any;
	public static grid_svg: string;

	private static app_alert_status: boolean = false;
	private static app_alert_message: string = '';

	constructor(options: Options) {

		// used by webpack to set line version
		//let WP_LIB_PATH: string;

		// pull in our environment from the header, so we can set dev options safely
		let env: string = $('meta[name="app-env"]').attr('content');

		if (env != '')
			Init.env = env;

		// capture any variables that are being sent to us from the app or querystring

		if (options.design_id) {
			// the numeric id of the design we are going to open
			Init.design_id = options.design_id;
		}

		if (options.series_id) {
			// the numeric id of the product line the tool will be configured for
			Init.series_id = Number(options.series_id);
		}

		if (options.builder_version) {
			// the version number of the build, to be passed to debug when errors are caught
			Init.builder_version = options.builder_version;
		}

		if (options.design_name) {
			// the alphanumeric name of this design, chosen by the user
			Init.design_name = options.design_name;
		}

		if (options.design_source) {
			// the location that the design data will be pulled from (web, office databases)
			Init.design_source = options.design_source;
		}
		if (options.design_source_host) {
			// the location that the design data will be pulled from (web, office databases)
			Init.design_source_host = options.design_source_host;
		}

		if (options.app_location) {
			// where the app is running (web, office)
			Init.app_location = options.app_location;
		}

		if (options.app_role) {
			// how the app is being used (preview, design, snapshot etc)
			Init.app_role = options.app_role;
		}

		Init.app_use = (Init.app_role == APP_ROLE.THUMB || Init.app_role == APP_ROLE.PREVIEW)
			? APP_USE.VIEW : APP_USE.DESIGN;


		if (options.my_cw_url) {
			// URL of the authentication portal so web users can sign up or sign in
			Init.my_cw_url = options.my_cw_url;
		}

		//console.log('Init.series_id', Init.series_id);
		// where our 3D application data lives, when we use the 3D popup
		if (Init.app_location == APP_LOCATION.WWW) {
			Init._3D_url = THREE_D_URL.WWW;
		} else if (Init.env == 'local') {
			Init._3D_url = THREE_D_URL.DEV;
		} else {
			Init._3D_url = THREE_D_URL.OFFICE;
		}

		Init.is_touch_device = ('ontouchstart' in window);
		//console.log(('ontouchstart' in window));

		// older design tools use inches for scale
		Init.grid_svg = (Init.series_id == SERIES_ID.RUSTIC) ? 'grid-cm' : 'grid-inch';

		new Setup();

		//  _WP_IMPORT_PATH is passed in at compile time from WebPack
		// @ts-ignore
		import(`@/line/${_WP_IMPORT_PATH}/LibInit.ts`)
			.then(({LibInit}) => {

				// load the specific library for the product line this tool is configured for
				Init.line = new LibInit();
				//console.log('LibInit', Init.grid_svg, Init.line.grid_svg);
				// the formal name of the product line, used in the display logo
				Init.line_name = Init.line.line_name;
				Init.line_color = Init.line.line_color;
				//Init.design_scale = Init.line.design_scale;
				Init.setStageColor(Init.line.stage_color);

				if (Init.app_role != APP_ROLE.THUMB) {

					// path to images used to show product items on the www designer
					Init.item_image_path += Init.line.item_image_dir + '/';

					Init.print_views = (Init.line.print_views) ? Init.line.print_views : Init.print_views;
					Init.print = Init.line.getPrint();

				}

			}).catch(error => console.log('Init', error));

	}


	static setStorage(key: string, value: string) {

		let seriesKey: string = key + '_' + Init.series_id;

		localStorage.setItem(seriesKey, value);

	}

	static getStorage(key: string) {

		let seriesKey: string = key + '_' + Init.series_id;

		return localStorage.getItem(seriesKey);

	}

	static setAppAlert(state: boolean = false, msg: string = '') {

		Init.app_alert_status = state;
		Init.app_alert_message = msg;

	}

	static getAppAlertStatus() {

		return Init.app_alert_status;

	}

	static getAppAlertMessage() {

		return Init.app_alert_message;

	}

	static setStageColor(color: string = '') {

		Init.stage_color = (Init.app_role != APP_ROLE.THUMB) ? color : '#FFF';

		$('.svg-grid').css('background-color', Init.stage_color);

	}


	static getDesignMeta() : {} {

		let meta: {} = {}

		let dimensions: Dimension = Init.manipulate.getDimensions(Init.svg_stage as svgjs.Container)
		meta['width'] = dimensions.w
		meta['depth'] = dimensions.d

		if (Init.user_interest == USER_INTEREST.COMMERCIAL) {
			let zone_size : number = Init.manipulate.getUseZoneSize(Init.series_id)
			meta['use_zone_width'] = dimensions.w + zone_size
			meta['use_zone_depth'] = dimensions.d + zone_size
		}

		//if (Init.indoor_outdoor == INDOOR_OUTDOOR.INDOOR) {
			let height : number = Init.manipulate.getDesignHeight()
			//console.log(height);
			meta['height'] = height
		//}

		meta['app_version'] = Init.app_version

		return meta
	}

}
